/** Stable NOLOS document doors; no source text, filenames or tokens in URLs. */
export interface DocumentRef {
  document_id: string
  display_title?: string | null
  page?: number | null
  snippet?: string | null
}
export interface ReaderContext {
  fundId?: string | null
  tab?: string | null
  focus?: string | null
  proposalId?: string | null
  annotationId?: string | null
  page?: number | null
  returnTo?: string | null
}
export function readerPage(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d+$/.test(value)
        ? Number(value)
        : NaN
  return Number.isSafeInteger(n) && n >= 1 ? n : null
}
export function documentReaderHref(id: string, context: ReaderContext = {}): string {
  if (!id || id.length > 200) throw new Error('Document identity is missing')
  const params = new URLSearchParams()
  if (context.fundId) params.set('fund', context.fundId)
  if (context.tab && ['record', 'evidence', 'overview'].includes(context.tab))
    params.set('tab', context.tab)
  if (
    context.focus &&
    /^(fund|(?:entity|instrument|segment|position|trade):[^\s]+)$/.test(context.focus)
  )
    params.set('focus', context.focus)
  if (context.proposalId) params.set('proposal', context.proposalId)
  if (context.annotationId) params.set('source', context.annotationId)
  const page = readerPage(context.page)
  if (page) params.set('page', String(page))
  if (
    context.returnTo &&
    /^\/v2(?:\/|\?|$)/.test(context.returnTo) &&
    !context.returnTo.includes('\\')
  )
    params.set('return', context.returnTo)
  const query = params.toString()
  return `/v2/documents/${encodeURIComponent(id)}${query ? `?${query}` : ''}`
}
/** Must run directly in the click, never after an awaited metadata/PDF fetch. */
export function openDocumentReader(id: string, context: ReaderContext = {}): void {
  window.open(documentReaderHref(id, context), '_blank', 'noopener,noreferrer')
}
/** Printing-equivalent double quotes; other characters keep their meaning. */
function quoteComparisonText(text: string): string {
  return text.replace(/[“”]/g, '"').toLowerCase()
}

/** Every actual quote occurrence, tolerating PDF whitespace and double-quote
 * typography. Other punctuation/digits stay exact; offsets refer to original text. */
export function quoteRanges(text: string, quote: string): { start: number; end: number }[] {
  let normalized = ''
  const offsets: number[] = []
  for (let i = 0; i < text.length; i++) {
    if (/\s/.test(text[i])) continue
    const part = quoteComparisonText(text[i])
    normalized += part
    for (let j = 0; j < part.length; j++) offsets.push(i)
  }
  const target = quoteComparisonText(quote.replace(/\s/g, ''))
  if (!target) return []
  const matches: { start: number; end: number }[] = []
  let from = 0
  while (from <= normalized.length - target.length) {
    const found = normalized.indexOf(target, from)
    if (found < 0) break
    matches.push({ start: offsets[found], end: offsets[found + target.length - 1] + 1 })
    from = found + target.length
  }
  return matches
}

export function readerContextFromHref(href: string | null): ReaderContext {
  const params = new URL(href ?? '/v2/traces', 'https://nolos.invalid').searchParams
  return {
    fundId: params.get('fund'),
    focus: params.get('focus'),
    tab: params.get('tab'),
    page: readerPage(params.get('page')),
    proposalId: params.get('proposal'),
    annotationId: params.get('source'),
    returnTo: params.get('return'),
  }
}

export interface OcrWord {
  text: string
  left: number
  top: number
  width: number
  height: number
  confidence: number
}
export interface OcrGeometry {
  render_width: number
  render_height: number
  words: OcrWord[]
  uncertain_words: number
  minimum_highlight_confidence: number
  rotation: number
}

/** Only real, in-bounds OCR word boxes at the stated confidence may be selected. */
export function ocrQuoteBoxes(geometry: OcrGeometry, quote: string): OcrWord[] {
  if (
    !Number.isFinite(geometry.render_width) ||
    !Number.isFinite(geometry.render_height) ||
    geometry.render_width <= 0 ||
    geometry.render_height <= 0 ||
    !Number.isFinite(geometry.minimum_highlight_confidence) ||
    geometry.minimum_highlight_confidence < 0 ||
    geometry.minimum_highlight_confidence > 100
  )
    return []
  let text = ''
  const indexed = geometry.words.map((word) => {
    if (text) text += ' '
    const start = text.length
    text += word.text
    return { word, start, end: text.length }
  })
  const boxes: OcrWord[] = []
  for (const match of quoteRanges(text, quote)) {
    const found = indexed.filter((item) => item.end > match.start && item.start < match.end)
    if (
      !found.length ||
      found.some(
        ({ word }) =>
          ![word.left, word.top, word.width, word.height, word.confidence].every(Number.isFinite) ||
          word.confidence < geometry.minimum_highlight_confidence ||
          word.confidence > 100 ||
          word.left < 0 ||
          word.top < 0 ||
          word.width <= 0 ||
          word.height <= 0 ||
          word.left + word.width > geometry.render_width ||
          word.top + word.height > geometry.render_height
      )
    )
      continue
    boxes.push(...found.map((item) => item.word))
  }
  return boxes
}
